import { ArrowUp, MessageSquare, User, Building2, Calendar, Tag } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { StandardProposal, useHasVoted, useVoteProposal } from '@/hooks/useProposals';
import { formatDistanceToNow } from 'date-fns';

interface ProposalCardProps {
  proposal: StandardProposal;
  commentCount?: number;
  onViewDetails: () => void;
}

const typeLabels: Record<string, string> = {
  field: 'Field Proposal',
  enum_value: 'Enum Value',
  standard: 'New Standard',
};

const typeBadgeColors: Record<string, string> = {
  field: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20',
  enum_value: 'bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/20',
  standard: 'bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20',
};

const statusLabels: Record<string, string> = {
  pending: 'Pending Review',
  under_review: 'Under Review',
  approved: 'Approved',
  rejected: 'Rejected',
  implemented: 'Implemented',
};

const statusBadgeColors: Record<string, string> = {
  pending: 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400 border-yellow-500/20',
  under_review: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20',
  approved: 'bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20',
  rejected: 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20',
  implemented: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20',
};

export function ProposalCard({ proposal, commentCount = 0, onViewDetails }: ProposalCardProps) {
  const hasVoted = useHasVoted(proposal.id);
  const voteMutation = useVoteProposal();

  const handleVote = (e: React.MouseEvent) => {
    e.stopPropagation();
    voteMutation.mutate({
      proposalId: proposal.id,
      action: hasVoted ? 'unvote' : 'vote',
    });
  };

  return (
    <Card 
      className="group hover:shadow-md transition-shadow cursor-pointer"
      onClick={onViewDetails}
    >
      <CardContent className="p-4 sm:p-6">
        <div className="flex gap-4">
          {/* Vote button */}
          <div className="flex flex-col items-center">
            <Button
              variant="ghost"
              size="sm"
              className={cn(
                'h-12 w-12 flex-col gap-0.5 rounded-lg',
                hasVoted 
                  ? 'bg-primary/10 text-primary hover:bg-primary/20' 
                  : 'hover:bg-muted'
              )}
              onClick={handleVote}
              disabled={voteMutation.isPending}
            >
              <ArrowUp className={cn('h-5 w-5', hasVoted && 'fill-current')} />
              <span className="text-sm font-semibold">{proposal.vote_count}</span>
            </Button>
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <Badge variant="outline" className={typeBadgeColors[proposal.proposal_type]}>
                {typeLabels[proposal.proposal_type]}
              </Badge>
              <Badge variant="outline" className={statusBadgeColors[proposal.status]}>
                {statusLabels[proposal.status]}
              </Badge>
            </div>

            <h3 className="text-lg font-semibold text-foreground mb-2 group-hover:text-primary transition-colors">
              {proposal.title}
            </h3>

            <p className="text-muted-foreground text-sm line-clamp-2 mb-3">
              {proposal.description}
            </p>

            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-muted-foreground">
              <div className="flex items-center gap-1.5">
                <User className="h-3.5 w-3.5" />
                <span>{proposal.submitter_name}</span>
              </div>
              
              {proposal.submitter_organization && (
                <div className="flex items-center gap-1.5">
                  <Building2 className="h-3.5 w-3.5" />
                  <span>{proposal.submitter_organization}</span>
                </div>
              )}

              <div className="flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5" />
                <span>{formatDistanceToNow(new Date(proposal.created_at), { addSuffix: true })}</span>
              </div>

              {proposal.target_category && (
                <div className="flex items-center gap-1.5">
                  <Tag className="h-3.5 w-3.5" />
                  <span>{proposal.target_category}</span>
                </div>
              )}

              {commentCount > 0 && (
                <div className="flex items-center gap-1.5">
                  <MessageSquare className="h-3.5 w-3.5" />
                  <span>{commentCount} {commentCount === 1 ? 'comment' : 'comments'}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
